import React, { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';
import { FilterBar } from '@/components/shared/FilterBar';
import { DataTable } from '@/components/shared/DataTable';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useParserJobs, useUpdateJob } from '@/hooks/useParserJobs';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Eye } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ManualParserQueue = () => {
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const updateJob = useUpdateJob();

  const filters = {
    ...activeFilters,
    progress_status: 'todo,in_progress,partial',
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
  };

  const { data, isLoading } = useParserJobs(filters);

  const filterConfigs = [
    {
      key: 'user_assigned',
      label: 'User Assigned',
      type: 'select' as const,
      options: [],
      placeholder: 'All users',
    },
    {
      key: 'progress_status',
      label: 'Progress Status',
      type: 'select' as const,
      options: [
        { value: 'todo', label: 'To Do' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'partial', label: 'Partial' },
        { value: 'completed', label: 'Completed' },
      ],
      placeholder: 'All statuses',
    },
  ];

  const columns = [
    {
      key: 'job_id',
      header: 'Job ID',
      render: (item: any) => (
        <span className="font-mono text-sm">{item.id.slice(0, 8)}</span>
      ),
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (item: any) => item.customer_name || 'N/A',
    },
    {
      key: 'supplier',
      header: 'Supplier',
      render: (item: any) => item.supplier_name || 'N/A',
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (item: any) => {
        const reason = item.reason || '';
        return (
          <span className="text-sm" title={reason}>
            {reason.length > 30 ? `${reason.slice(0, 30)}...` : reason || '-'}
          </span>
        );
      },
    },
    {
      key: 'user_assigned',
      header: 'User Assigned',
      render: (item: any) => (
        <Select
          value={item.user_assigned || 'unassigned'}
          onValueChange={(value) => {
            updateJob.mutate({
              jobId: item.id,
              updates: { user_assigned: value === 'unassigned' ? null : value },
            });
          }}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unassigned">Unassigned</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (item: any) => (
        <Select
          value={item.progress_status || 'todo'}
          onValueChange={(value) => {
            updateJob.mutate({
              jobId: item.id,
              updates: { progress_status: value },
            });
          }}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todo">To Do</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      key: 'invoice',
      header: 'Invoice',
      render: (item: any) => {
        if (!item.file_url) return 'N/A';
        return (
          <Button variant="ghost" size="sm" asChild>
            <a href={item.file_url} target="_blank" rel="noopener noreferrer">
              <Eye className="h-4 w-4" />
            </a>
          </Button>
        );
      },
    },
    {
      key: 'action',
      header: 'Action',
      render: (item: any) => (
        <Button
          size="sm"
          onClick={() =>
            navigate(`/w/${currentWorkspace?.id}/dashboard/parser/manual/${item.id}`)
          }
        >
          Parse
        </Button>
      ),
    },
  ];

  return (
    <ModuleAccessGuard moduleId="bill_processing">
      <div className="container mx-auto py-6 space-y-6">
        <PageHeader
          title="Manual Parser Queue"
          description="Bills requiring manual data entry with assignment"
        />

        <FilterBar
          searchPlaceholder=""
          searchValue=""
          onSearchChange={() => {}}
          filters={filterConfigs}
          activeFilters={activeFilters}
          onFilterChange={(key, value) => setActiveFilters({ ...activeFilters, [key]: value })}
          onClearFilters={() => setActiveFilters({})}
        />

        <DataTable
          data={data?.items || []}
          columns={columns}
          totalCount={data?.total || 0}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          emptyMessage="No manual parser jobs found"
        />
      </div>
    </ModuleAccessGuard>
  );
};

export default ManualParserQueue;